<template>
  <v-navigation-drawer :rail="isCollapsed" permanent :color="drawerBackground" floating elevation="0" :width="240"
    class="app-sidebar" :key="redraw">
    <v-list density="compact" nav class="px-2 pt-0 pb-4">
      <template v-for="(group, index) in displayGroups" :key="group.key">
        <!-- Admin sections are picked with tabs instead of being stacked -->
        <v-tabs v-if="group.isTabs" v-model="activeAdminTab" :direction="isCollapsed ? 'vertical' : 'horizontal'"
          :grow="!isCollapsed" density="compact" color="primary" class="admin-tabs mt-2 mb-1"
          :class="{ 'admin-tabs-rail': isCollapsed }">
          <v-tab v-for="tab in adminNavGroups" :key="tab.title" :value="tab.title" class="admin-tab">
            <v-icon v-if="isCollapsed">{{ tab.icon }}</v-icon>
            <span v-else class="text-uppercase font-weight-black text-caption">{{ tab.title }}</span>
            <v-tooltip v-if="isCollapsed" activator="parent" location="right">{{ tab.title }}</v-tooltip>
          </v-tab>
        </v-tabs>

        <!-- Group Subheader -->
        <v-list-subheader v-else-if="!isCollapsed && group.items.length > 0"
          class="text-uppercase font-weight-black text-caption mt-2 opacity-60">
          {{ categoryLabel(group.title) }}
        </v-list-subheader>

        <v-divider v-if="isCollapsed && index > 0 && !group.isTabs && group.items.length > 0" class="my-1 mx-4"></v-divider>

        <!-- Sections inside a group carry their own heading; a group without them renders one plain run -->
        <template v-for="(section, sectionIndex) in group.sections" :key="`${group.key}-${section.title || sectionIndex}`">
          <v-list-subheader v-if="section.title && !isCollapsed"
            class="text-uppercase font-weight-black text-caption mt-2 opacity-60">
            {{ categoryLabel(section.title) }}
          </v-list-subheader>

          <v-divider v-if="section.title && isCollapsed && sectionIndex > 0" class="my-1 mx-4"></v-divider>

          <template v-for="item in section.items" :key="item.value">
            <v-list-item :prepend-icon="item.value === 'whatsnew' && hasNewBlog ? undefined : item.icon" :title="navLabel(item.title)"
              :value="item.value" :active="isActive(item)" @click="handleItemClick(item)"
              rounded="lg" class="nav-item" active-class="active-nav-item">
              <template v-slot:prepend v-if="item.value === 'whatsnew' && hasNewBlog">
                <v-badge dot color="error" offset-x="2" offset-y="2">
                  <v-icon>{{ item.icon }}</v-icon>
                </v-badge>
              </template>

              <!-- Only rendered when it holds something: an empty append still reserves a 20px column -->
              <template v-slot:append v-if="!isCollapsed && ((item.value === 'companies' && isAdmin) || (item.value === 'whatsnew' && hasNewBlog))">
                <v-btn v-if="item.value === 'companies' && !isCollapsed && isAdmin" icon="mdi-magnify" variant="text" size="small" @click.stop="openCompanySearchDialog"></v-btn>
                <v-chip v-if="item.value === 'whatsnew' && hasNewBlog && !isCollapsed" color="error" size="x-small" variant="flat" class="font-weight-bold ml-2">{{ $t('shell_nav_new_chip') }}</v-chip>
              </template>

              <!-- Tooltip for collapsed state -->
              <v-tooltip v-if="isCollapsed" activator="parent" location="right">
                {{ item.value === 'whatsnew' && hasNewBlog ? $t('shell_nav_title_new', navLabel(item.title)) : navLabel(item.title) }}
              </v-tooltip>
            </v-list-item>

            <!-- The rail clips the append slot, so company search gets its own icon when collapsed -->
            <v-list-item v-if="item.value === 'companies' && isCollapsed && isAdmin" prepend-icon="mdi-magnify"
              title="Search Companies" @click="openCompanySearchDialog" rounded="lg" class="nav-item">
              <v-tooltip activator="parent" location="right">Search Companies</v-tooltip>
            </v-list-item>
          </template>
        </template>
      </template>
    </v-list>

    <template v-slot:append>
      <div class="px-2 py-1 border-t sidebar-footer">
        <!-- Theme Toggle -->
        <v-btn v-if="showModeButton" block variant="tonal" :icon="isCollapsed" class="mb-1" @click="toggleTheme" size="small">
          <v-icon v-if="isCollapsed">{{ isDarkTheme ? 'mdi-white-balance-sunny' : 'mdi-weather-night' }}</v-icon>
          <template v-else>
            <v-icon start>{{ isDarkTheme ? 'mdi-white-balance-sunny' : 'mdi-weather-night' }}</v-icon>
            {{ isDarkTheme ? $t('shell_nav_light_mode') : $t('shell_nav_dark_mode') }}
          </template>
        </v-btn>

        <!-- Shrink / expand: the arrows point the way the sidebar will move -->
        <v-list-item :prepend-icon="isCollapsed ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left'"
          :title="isCollapsed ? $t('shell_nav_expand') : $t('shell_nav_collapse')" @click="emit('toggleSidebar')"
          rounded="lg" density="compact" class="nav-item sidebar-toggle">
          <v-tooltip v-if="isCollapsed" activator="parent" location="right">{{ $t('shell_nav_expand') }}</v-tooltip>
        </v-list-item>
      </div>
    </template>

    <!-- Scroll Indicator Hint -->
    <v-fade-transition>
      <div v-if="showScrollHint && !isCollapsed" class="scroll-hint">
        <v-icon color="primary" size="small" class="mb-1">mdi-chevron-double-down</v-icon>
        <span class="text-caption font-weight-bold ml-1">{{ $t('shell_nav_more') }}</span>
      </div>
    </v-fade-transition>
  </v-navigation-drawer>

  <!-- Company Search Dialog -->
  <CompanySearchDialog v-if="showCompanySearch" :companies="allCompanies" :loading="loadingCompanies" @closeDialog="showCompanySearch = false" />
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useTheme } from 'vuetify';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useConnection } from '../composables/useConnection';
import { useCompany } from '../composables/useCompany';
import { useBlogIndicator } from '../composables/useBlogIndicator';
import CompanySearchDialog from './dialogs/CompanySearchDialog.vue';
import { t } from '@/i18n/index.js';

// Item titles and categories below stay English: they are matched in code and sent as
// analytics names. These maps give the label shown; admin-only entries (RC Admin,
// Operator Context's own items) are staff tooling and stay English.
const NAV_LABELS = {
  'Dashboard': () => t('common_dashboard'),
  'Compliance': () => t('shell_nav_compliance'),
  'Map': () => t('app_map_title'),
  'Blog': () => t('shell_nav_blog'),
  'Inspections': () => t('app_settings_inspections'),
  'Hours of Service': () => t('hos_title'),
  'Reports': () => t('shell_nav_reports'),
  'Vehicles & Trailers': () => t('shell_nav_vehicles_trailers'),
  'Equipment': () => t('vehicle_equipment_label'),
  'Users': () => t('user_title'),
  'Checklists': () => t('common_checklists'),
  'GPS Devices': () => t('shell_nav_gps_devices'),
  'Company': () => t('labelCompany'),
  'Invoices': () => t('common_invoices'),
  'Billing': () => t('shell_nav_billing'),
};
const CATEGORY_LABELS = {
  'Overview': () => t('shell_nav_overview'),
  'Operations': () => t('shell_nav_operations'),
  'Assets': () => t('shell_nav_assets'),
  'Settings': () => t('app_tab_settings'),
  'Administration': () => t('shell_nav_administration'),
};
const navLabel = (title) => (NAV_LABELS[title] ? NAV_LABELS[title]() : title);
const categoryLabel = (title) => (CATEGORY_LABELS[title] ? CATEGORY_LABELS[title]() : title);

const route = useRoute();
const store = useStore();
const { currentCompany, withAdminCompany } = useConnection();
const { hasNewBlog, markBlogAsRead } = useBlogIndicator();

const domain = computed(() => store.getters.domain);
const redraw = ref(0);
const showCompanySearch = ref(false);
const { loadedCompanyList, loadCompaniesState, loadAllCompanies } = useCompany(domain);

const allCompanies = computed(() => {
  return loadedCompanyList.value.map(c => ({
    id: c.id(),
    name: c.name(),
    obj: c,
  }));
});

const loadingCompanies = computed(() => loadCompaniesState.value === 'loading');
const showModeButton = ref(false);
const showScrollHint = ref(false);
let drawerContent = null;

const checkScroll = () => {
  if (!drawerContent) return;
  const { scrollTop, scrollHeight, clientHeight } = drawerContent;
  // Show hint if there is more than 20px of scrollable content left
  showScrollHint.value = scrollHeight > clientHeight && (scrollTop + clientHeight < scrollHeight - 20);
};

const setupScrollListener = () => {
  if (drawerContent) {
    drawerContent.removeEventListener('scroll', checkScroll);
  }
  drawerContent = document.querySelector('.app-sidebar .v-navigation-drawer__content');
  if (drawerContent) {
    drawerContent.addEventListener('scroll', checkScroll);
    checkScroll();
  }
};

onMounted(() => {
  // Use a slight delay to ensure Vuetify has rendered the drawer
  setTimeout(setupScrollListener, 300);
  window.addEventListener('resize', checkScroll);
  setTimeout(scrollActiveIntoView, 500);
});

onUnmounted(() => {
  if (drawerContent) {
    drawerContent.removeEventListener('scroll', checkScroll);
  }
  window.removeEventListener('resize', checkScroll);
});

watch(redraw, () => {
  setTimeout(setupScrollListener, 300);
});

const openCompanySearchDialog = () => {
  showCompanySearch.value = true;
  if (allCompanies.value.length === 0) {
    loadAllCompanies();
  }
};


const props = defineProps({
  isCollapsed: {
    type: Boolean,
    default: false,
  },
  isMechanic: {
    type: Boolean,
    default: false,
  },
  isOwnerOrManager: {
    type: Boolean,
    default: false,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  isLocalDev: {
    type: Boolean,
    default: false,
  },
  features: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['navigate', 'toggleSidebar']);

// Widening the sidebar reveals headings and can push items out of view; re-check once the 0.3s width transition ends
watch(() => props.isCollapsed, () => {
  setTimeout(checkScroll, 350);
});

const theme = useTheme();

// Navigation items with categories
const allNavItems = () => {
  let list = [
    // --- READYCHEK CUSTOMER ---
    // --- OVERVIEW ---
    { title: 'Dashboard', icon: 'mdi-view-dashboard-outline', value: 'dashboard', requiresRole: 'mechanic', version: 'v3', category: 'Overview' },
    { title: 'Dashboard', icon: 'mdi-view-dashboard-outline', value: 'dashboard', requiresRole: 'default', version: 'v3', category: 'Overview' },
    { title: 'Compliance', icon: 'mdi-shield-check-outline', value: 'compliance', requiresRole: 'default', requiresFeature: 'inspections|timesheets', version: 'v3', category: 'Overview' },
    { title: 'Map', icon: 'mdi-map-marker-outline', value: 'map', requiresRole: 'default', version: 'v3', requiresFeature: 'map|gpsDevice', category: 'Overview' },
    { title: 'Map', icon: 'mdi-map-marker-outline', value: 'map', requiresRole: 'mechanic', version: 'v3', requiresFeature: 'map|gpsDevice', category: 'Overview' },
    { title: 'Blog', icon: 'mdi-bullhorn-outline', value: 'whatsnew', requiresRole: 'mechanic', version: 'v3', type: 'internal', href: 'https://readychek.com/blog', category: 'Overview' },
    { title: 'Blog', icon: 'mdi-bullhorn-outline', value: 'whatsnew', requiresRole: 'default', version: 'v3', type: 'internal', href: 'https://readychek.com/blog', category: 'Overview' },

    // --- OPERATIONS ---
    { title: 'Inspections', icon: 'mdi-clipboard-check-outline', value: 'inspections', requiresRole: 'mechanic', requiresFeature: 'inspections|equipment', version: 'v3', category: 'Operations' },
    { title: 'Inspections', icon: 'mdi-clipboard-check-outline', value: 'inspections', requiresRole: 'default', requiresFeature: 'inspections|equipment', version: 'v3', category: 'Operations' },
    { title: 'Hours of Service', icon: 'mdi-clock-outline', value: 'hours', requiresRole: 'default', requiresFeature: 'timesheets', version: 'v3', category: 'Operations' },
    { title: 'Reports', icon: 'mdi-file-chart-outline', value: 'reports', requiresRole: 'mechanic', requiresFeature: 'inspections', version: 'v3', category: 'Operations' },
    { title: 'Reports', icon: 'mdi-file-chart-outline', value: 'reports', requiresRole: 'default', requiresFeature: 'inspections', version: 'v3', category: 'Operations' },

    // --- FLEET ---
    { title: 'Vehicles & Trailers', icon: 'mdi-truck-outline', value: 'vehicles', requiresRole: 'mechanic', requiresFeature: 'vehicles', version: 'v3', category: 'Assets' },
    { title: 'Vehicles & Trailers', icon: 'mdi-truck-outline', value: 'vehicles', requiresRole: 'default', requiresFeature: 'vehicles', version: 'v3', category: 'Assets' },
    { title: 'Equipment', icon: 'mdi-excavator', value: 'equipment', requiresRole: 'mechanic', requiresFeature: 'equipment', version: 'v3', category: 'Assets' },
    { title: 'Equipment', icon: 'mdi-excavator', value: 'equipment', requiresRole: 'default', requiresFeature: 'equipment', version: 'v3', category: 'Assets' },
    
    // --- Settings ---
    { title: 'Users', icon: 'mdi-account-group-outline', value: 'users', requiresRole: 'default', version: 'v3', category: 'Settings' },
    { title: 'Checklists', icon: 'mdi-format-list-checks', value: 'checklists', requiresRole: 'mechanic', requiresFeature: 'checklists|equipment', version: 'v3', category: 'Settings' },
    { title: 'Checklists', icon: 'mdi-format-list-checks', value: 'checklists', requiresRole: 'default', requiresFeature: 'checklists|equipment', version: 'v3', category: 'Settings' },
    { title: 'GPS Devices', icon: 'mdi-map-marker-radius-outline', value: 'gps/devices', requiresRole: 'mechanic', requiresFeature: 'gpsDevice', version: 'v3', category: 'Settings' },
    { title: 'GPS Devices', icon: 'mdi-map-marker-radius-outline', value: 'gps/devices', requiresRole: 'default', requiresFeature: 'gpsDevice', version: 'v3', category: 'Settings' },
    { title: 'Company', icon: 'mdi-domain', value: 'company/display', requiresRole: 'default', version: 'v3', category: 'Settings' },

    // --- ADMINISTRATION ---
    { title: 'Invoices', icon: 'mdi-receipt-text-outline', value: 'invoices', requiresRole: 'default', version: 'v3', category: 'Administration' },
    { title: 'Billing', icon: 'mdi-credit-card-outline', value: 'billing/display', requiresRole: 'default', version: 'v3', category: 'Administration' },
  ];

  let rcAdmin = [
    // --- READYCHEK ADMIN ---
    { title: 'Companies', icon: 'mdi-domain-switch', value: 'companies', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Invoices', icon: 'mdi-receipt-text-outline', value: 'invoices', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Registrations', icon: 'mdi-new-box', value: 'registrations', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Health', icon: 'mdi-bottle-tonic-plus-outline', value: 'health', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Map', icon: 'mdi-map-marker-outline', value: 'map', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Stats', icon: 'mdi-chart-bar', value: 'stats', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Users', icon: 'mdi-account-group-outline', value: 'users', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'GPS Devices', icon: 'mdi-map-marker-radius-outline', value: 'gps/devices', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Inspections', icon: 'mdi-clipboard-check-outline', value: 'inspections', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Checklists', icon: 'mdi-format-list-checks', value: 'checklists', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Ai Prompts', icon: 'mdi-robot-outline', value: 'llm/prompts', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Lost Passwords', icon: 'mdi-lock-reset', value: 'passwords', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Cards', icon: 'mdi-card-outline', value: 'invoice/settings', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    { title: 'Schedules', icon: 'mdi-check-all', value: 'schedules', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
    // { title: 'Cards', icon: 'mdi-card-outline', value: 'cards', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'RC Admin' },
  ];

  let rcAccountAdmin = [
    // --- RC ADMIN OPERATOR CONTEXT ---
    { title: 'Configuration', icon: 'mdi-cog-outline', value: 'accounts/:operatorId/configuration', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'Operator Context' },

    // --- OVERVIEW ---
    { title: 'Dashboard', icon: 'mdi-view-dashboard-outline', value: 'accounts/:operatorId/dashboard', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'Operator Context', section: 'Overview' },
    { title: 'Compliance', icon: 'mdi-shield-check-outline', value: 'accounts/:operatorId/compliance', requiresRole: 'admin', requiresFeature: 'inspections|timesheets', version: 'v3', content: 'admin', category: 'Operator Context', section: 'Overview' },
    { title: 'Map', icon: 'mdi-map-marker-outline', value: 'accounts/:operatorId/map', requiresRole: 'admin', requiresFeature: 'map|gpsDevice', version: 'v3', content: 'admin', category: 'Operator Context', section: 'Overview' },

    // --- OPERATIONS ---
    { title: 'Inspections', icon: 'mdi-clipboard-check-outline', value: 'accounts/:operatorId/inspections', requiresRole: 'admin', version: 'v3', requiresFeature: 'inspections|equipment', content: 'admin', category: 'Operator Context', section: 'Operations' },
    { title: 'Hours of Service', icon: 'mdi-clock-outline', value: 'accounts/:operatorId/hours', requiresRole: 'admin', version: 'v3', requiresFeature: 'timesheets', content: 'admin', category: 'Operator Context', section: 'Operations' },
    { title: 'Reports', icon: 'mdi-file-chart-outline', value: 'accounts/:operatorId/reports', requiresRole: 'admin', version: 'v3', requiresFeature: 'inspections', content: 'admin', category: 'Operator Context', section: 'Operations' },

    // --- FLEET ---
    { title: 'Vehicles & Trailers', icon: 'mdi-truck-outline', value: 'accounts/:operatorId/vehicles', requiresRole: 'admin', version: 'v3', requiresFeature: 'vehicles', content: 'admin', category: 'Operator Context', section: 'Assets' },
    { title: 'Equipment', icon: 'mdi-excavator', value: 'accounts/:operatorId/equipment', requiresRole: 'admin', requiresFeature: 'equipment', version: 'v3', content: 'admin', category: 'Operator Context', section: 'Assets' },

    // --- Settings ---
    { title: 'Users', icon: 'mdi-account-group-outline', value: 'accounts/:operatorId/users', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'Operator Context', section: 'Settings' },
    { title: 'Checklists', icon: 'mdi-format-list-checks', value: 'accounts/:operatorId/checklists', requiresRole: 'admin', version: 'v3', requiresFeature: 'checklists|equipment', content: 'admin', category: 'Operator Context', section: 'Settings' },
    { title: 'GPS Devices', icon: 'mdi-map-marker-radius-outline', value: 'accounts/:operatorId/gps/devices', requiresRole: 'admin', version: 'v3', requiresFeature: 'gpsDevice', content: 'admin', category: 'Operator Context', section: 'Settings' },
    { title: 'Company', icon: 'mdi-domain', value: 'accounts/:operatorId/display', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'Operator Context', section: 'Settings' },

    // --- ADMINISTRATION ---
    { title: 'Invoices', icon: 'mdi-receipt-text-outline', value: 'accounts/:operatorId/invoices', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'Operator Context', section: 'Administration' },
    { title: 'Billing', icon: 'mdi-credit-card-outline', value: 'accounts/:operatorId/billing/display', requiresRole: 'admin', version: 'v3', content: 'admin', category: 'Operator Context', section: 'Administration' },
  ];

  list.push(...rcAdmin);
  if (domain.value.session().company().isNotNew()) {
    list.push(...rcAccountAdmin);
  }
  return list;
};

const visibleNavItems = computed(() => {
  return allNavItems().filter(item => {
    if (item.requiresRole === 'mechanic' && !props.isMechanic) return false;
    if (item.requiresRole === 'default' && !props.isOwnerOrManager) return false;
    if (item.requiresRole === 'admin' && !props.isAdmin) return false;
    if (item.requiresRole === 'dev' && !props.isLocalDev) return false;

    var requiresFeature = item.requiresFeature;
    let containsAtLeastOne = false;
    if (requiresFeature != null) {
      var requiresFeatures = requiresFeature.split('|');
      for (let i = 0; i < requiresFeatures.length; i++) {
        let feature = requiresFeatures[i];
        if (props.features[feature]) {
          containsAtLeastOne = true;
          break;
        }
      }
    }

    if (item.requiresFeature && !containsAtLeastOne) {
      return false;
    }

    return true;
  });
});

// An item may carry a section so one category can show titled runs inside it,
// the way the operator context mirrors the customer sidebar's headings.
// Items without a section lead the list under no heading.
const buildSections = (items) => {
  const sections = [];
  items.forEach(item => {
    const title = item.section || null;
    const last = sections[sections.length - 1];
    if (last && last.title === title) {
      last.items.push(item);
    } else {
      sections.push({ title, items: [item] });
    }
  });
  return sections;
};

const groupedNavItems = computed(() => {
  const groups = [];
  const items = visibleNavItems.value;

  // Define the order of categories
  const categories = ['Overview', 'Operations', 'Assets', 'Settings', 'Administration', 'RC Admin', 'Operator Context'];

  categories.forEach(cat => {
    const catItems = items.filter(i => i.category === cat);
    if (catItems.length > 0) {
      groups.push({
        title: cat,
        items: catItems,
        sections: buildSections(catItems)
      });
    }
  });

  return groups;
});

// Admin navigation is split across tabs instead of stacked groups.
// 'Operator Context' only exists once a company has been selected.
const ADMIN_CATEGORIES = ['RC Admin', 'Operator Context'];
const ADMIN_CATEGORY_ICONS = {
  'RC Admin': 'mdi-shield-crown-outline',
  'Operator Context': 'mdi-domain',
};

const activeAdminTab = ref(ADMIN_CATEGORIES[0]);

const adminNavGroups = computed(() => {
  return groupedNavItems.value
    .filter(g => ADMIN_CATEGORIES.includes(g.title))
    .map(g => ({ ...g, icon: ADMIN_CATEGORY_ICONS[g.title] }));
});

const activeAdminGroup = computed(() => {
  const groups = adminNavGroups.value;
  return groups.find(g => g.title === activeAdminTab.value) || groups[0] || null;
});

const displayGroups = computed(() => {
  const groups = groupedNavItems.value
    .filter(g => !ADMIN_CATEGORIES.includes(g.title))
    .map(g => ({ key: g.title, title: g.title, items: g.items, sections: g.sections, isTabs: false }));

  if (activeAdminGroup.value) {
    groups.push({
      key: 'admin-tabs',
      title: activeAdminGroup.value.title,
      items: activeAdminGroup.value.items,
      sections: activeAdminGroup.value.sections,
      isTabs: true,
    });
  }

  return groups;
});

// Selecting a company reveals the Operator Context tab, so switch to it;
// clearing the company drops back to the RC Admin tab.
watch(() => adminNavGroups.value.some(g => g.title === 'Operator Context'), (hasOperatorContext) => {
  activeAdminTab.value = hasOperatorContext ? 'Operator Context' : 'RC Admin';
});

const drawerBackground = computed(() => {
  return theme.global.name.value === 'dark' ? '#141414' : '#F4F7F9';
});

const isDarkTheme = computed(() => theme.global.name.value === 'dark');

function toggleTheme() {
  const newTheme = theme.global.name.value === 'dark' ? 'light' : 'dark';
  theme.change(newTheme);
  localStorage.setItem('rc-theme', newTheme);
}

function handleItemClick(item) {
  if (item.value === 'whatsnew') {
    markBlogAsRead();
  }
  if (item.type === 'external') {
    window.open(item.href, '_blank');
  } else {
    emit('navigate', { value: item.value, version: item.version, content: item.content ? item.content : "" });
  }
}

const isActive = (item) => {
  if (!domain.value || !domain.value.session()) return false;
  
  const company = domain.value.session().company();
  if (!company || company.isNew()) return false;
  
  const companyId = company.idEncoded();
  let rolePath = 'operator';
  let pathValue = item.value;
  let content = item.content;
  let subPath = '';
  
  if (item.type === 'external') {
    return false;
  }
  
  if (content === "company" || content === "" || content == undefined) {
    rolePath = "operator/";
    subPath = `${rolePath}${companyId}/${pathValue}`;
  } else if (pathValue.startsWith("accounts/:operatorId")) {
    rolePath = "admin/";
    subPath = `${rolePath}${pathValue.replace(":operatorId", companyId)}`;
  } else {
    rolePath = "admin";
    subPath = `${rolePath}/${pathValue}`;
  }
  
  let expectedPath = '';
  if (item.version === 'v3') {
    expectedPath = `/portal/v3/${subPath}`;
  } else {
    expectedPath = `/portal/${rolePath}/${companyId}/${pathValue}`;
  }
  
  const currentPath = route.path.toLowerCase();
  expectedPath = expectedPath.toLowerCase();
  
  return currentPath === expectedPath || currentPath.startsWith(expectedPath + '/');
};

const scrollActiveIntoView = () => {
  nextTick(() => {
    setTimeout(() => {
      const activeEl = document.querySelector('.app-sidebar .active-nav-item');
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 150);
  });
};

watch(() => route.path, (newPath) => {
  scrollActiveIntoView();
  if (newPath && newPath.toLowerCase().includes('/admin/accounts/')
    && adminNavGroups.value.some(g => g.title === 'Operator Context')) {
    activeAdminTab.value = 'Operator Context';
  }
  if (newPath && newPath.toLowerCase().includes('whatsnew')) {
    markBlogAsRead();
  }
}, { immediate: true });

watch(currentCompany, (newCompany) => {
  if (newCompany) {
    domain.value.session().with(newCompany);
  } else {
    withAdminCompany();
    domain.value.session().withoutCompany();
  }
  redraw.value = redraw.value + 1;
  setTimeout(scrollActiveIntoView, 400);
});

</script>

<style scoped>
.app-sidebar {
  border-right: 1px solid rgba(var(--v-border-color), 0.12) !important;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.nav-item {
  transition: all 0.2s ease;
  min-height: 32px !important;
  margin-bottom: 2px !important;
}

/* Vuetify's 32px gap between icon and label; the icon itself stays put, in line with the rail */
.nav-item :deep(.v-list-item__spacer) {
  width: 20px !important;
}

.nav-item :deep(.v-list-item-title) {
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.active-nav-item {
  background: rgba(var(--v-theme-primary), 0.08) !important;
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 600 !important;
}

.active-nav-item :deep(.v-icon) {
  color: rgb(var(--v-theme-primary)) !important;
}

.sidebar-header {
  height: 72px;
}

.sidebar-footer {
  background: transparent;
  border-top: 1px solid rgba(var(--v-border-color), 0.08) !important;
}

.sidebar-toggle {
  margin-bottom: 0 !important;
  /* Outside the nav list it gets 16px padding; match the list so its icon lines up with the menu's */
  padding-inline: 8px !important;
  opacity: 0.7;
}

.sidebar-toggle:hover {
  opacity: 1;
}

.admin-tabs {
  border-bottom: 1px solid rgba(var(--v-border-color), 0.12);
}

.admin-tab {
  min-width: 0 !important;
  padding: 0 8px !important;
  letter-spacing: 0.08em !important;
  opacity: 0.6;
}

.admin-tab.v-tab--selected {
  opacity: 1;
}

.admin-tabs-rail {
  border-bottom: none;
}

.admin-tabs-rail .admin-tab {
  min-height: 32px !important;
}

:deep(.v-list-subheader) {
  color: rgba(var(--v-theme-on-surface), 0.4) !important;
  font-weight: 800 !important;
  letter-spacing: 0.12em !important;
  font-size: 0.65rem !important;
  min-height: 24px !important;
}

/* Custom scrollbar for the sidebar */
:deep(.v-navigation-drawer__content)::-webkit-scrollbar {
  width: 5px;
}

:deep(.v-navigation-drawer__content)::-webkit-scrollbar-thumb {
  background: rgba(var(--v-border-color), 0.1);
  border-radius: 10px;
}

:deep(.v-navigation-drawer__content)::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-border-color), 0.2);
}

.scroll-hint {
  position: absolute;
  /* Clears the footer, which always holds the shrink/expand button */
  bottom: 41px;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(to top, v-bind(drawerBackground) 40%, transparent);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  pointer-events: none;
  z-index: 5;
  color: rgb(var(--v-theme-primary));
  padding-bottom: 8px;
}
</style>
